import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import axiosInstance from "../../api/axiosInstance";

const api = {
  list: (q = "") =>
    axiosInstance.get("/api/superadmin/case-study-industries/", {
      params: q ? { q } : {},
    }),
  create: (body) =>
    axiosInstance.post("/api/superadmin/case-study-industries/", body, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, body) =>
    axiosInstance.patch(`/api/superadmin/case-study-industries/${id}/`, body, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  remove: (id) =>
    axiosInstance.delete(`/api/superadmin/case-study-industries/${id}/`),
};
const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => <span>✎</span>;
const SearchIcon = () => <span>⌕</span>;
const StatusBadge = ({ active }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      background: active ? "#dcfce7" : "#fee2e2",
      color: active ? "#16a34a" : "#dc2626",
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: active ? "#22c55e" : "#f43f5e",
      }}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

const Skeleton = ({ width, height = 14 }) => (
  <div
    className="industry-skeleton"
    style={{ width, height, borderRadius: 6 }}
  />
);

function IndustryTableSkeleton() {
  return Array.from({ length: 6 }, (_, index) => (
    <tr key={`industry-skeleton-${index}`} style={{ borderTop: "1px solid #f1f5f9" }}>
      <td style={{ padding: "12px 20px" }}><Skeleton width={92} height={42} /></td>
      <td style={{ padding: "12px 16px" }}><Skeleton width={38} height={38} /></td>
      <td style={{ padding: "12px 16px" }}><Skeleton width="62%" /><div style={{ marginTop: 7 }}><Skeleton width="42%" height={12} /></div></td>
      <td style={{ padding: "12px 16px" }}><Skeleton width={68} height={24} /></td>
      <td style={{ padding: "12px 16px" }}><Skeleton width={80} height={30} /></td>
      <td style={{ padding: "12px 20px", textAlign: "right" }}><div style={{ display: "flex", justifyContent: "flex-end", gap: 7 }}><Skeleton width={32} height={30} /><Skeleton width={82} height={30} /></div></td>
    </tr>
  ));
}

function Modal({ title, children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(15,23,42,.5)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 24px 64px rgba(0,0,0,.18)",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 17, color: "#0f172a" }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              border: 0,
              background: "none",
              color: "#64748b",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: "20px 24px 24px" }}>{children}</div>
      </div>
    </div>
  );
}

const INDUSTRY_IMAGE_WIDTH = 1254;
const INDUSTRY_IMAGE_HEIGHT = 1254;

const createCroppedIndustryImage = (source, croppedAreaPixels) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = INDUSTRY_IMAGE_WIDTH;
      canvas.height = INDUSTRY_IMAGE_HEIGHT;
      const context = canvas.getContext("2d");
      context.drawImage(image, croppedAreaPixels.x, croppedAreaPixels.y, croppedAreaPixels.width, croppedAreaPixels.height, 0, 0, INDUSTRY_IMAGE_WIDTH, INDUSTRY_IMAGE_HEIGHT);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Could not create the cropped image."));
          return;
        }
        resolve(new File([blob], "industry-image-1254x1254.jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.92);
    };
    image.onerror = () => reject(new Error("Could not load the selected image."));
    image.src = source;
  });

function IndustryImageCropper({ file, onComplete, onCancel }) {
  const [imageSource, setImageSource] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setImageSource(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const applyCrop = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    setError("");
    try {
      const croppedFile = await createCroppedIndustryImage(imageSource, croppedAreaPixels);
      onComplete(croppedFile);
    } catch (cropError) {
      setError(cropError.message || "Could not crop the image.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Crop industry image" style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(15,23,42,.72)" }}>
      <div style={{ width: "min(720px, 100%)", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,.24)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ margin: 0, fontSize: 17, color: "#0f172a" }}>Crop industry image</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#64748b" }}>Position the image inside the square frame. It will be saved at 1254 × 1254 px.</p>
        </div>
        <div style={{ position: "relative", height: 360, background: "#0f172a" }}>
          {imageSource && <Cropper image={imageSource} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)} />}
        </div>
        <div style={{ padding: "16px 20px 20px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#475569" }}>Zoom<input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} style={{ flex: 1, accentColor: "#3b82f6" }} /></label>
          {error && <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "#dc2626" }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 18 }}>
            <button type="button" onClick={onCancel} disabled={processing} style={{ padding: "8px 15px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}>Cancel</button>
            <button type="button" onClick={applyCrop} disabled={processing} style={{ padding: "8px 15px", borderRadius: 8, border: 0, background: "#2563eb", color: "#fff", fontWeight: 600, cursor: processing ? "wait" : "pointer" }}>{processing ? "Cropping..." : "Use cropped image"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IndustryForm({ initial, saving, onSubmit, onCancel }) {
  const [form, setForm] = useState({ name: initial?.name || "", description: initial?.description || "" });
  const [files, setFiles] = useState({});
  const [imageToCrop, setImageToCrop] = useState(null);
  const input = {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    color: "#1e293b",
  };
  return (
    <>
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form, files);
      }}
    >
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          marginBottom: 5,
        }}
      >
        Industry name *
      </label>
      <input
        required
        autoFocus
        style={input}
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          margin: "15px 0 5px",
        }}
      >
        Industry description
      </label>
      <input
        style={input}
        value={form.description}
        placeholder="e.g. Building future horizons"
        maxLength={255}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
      />
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          margin: "15px 0 5px",
        }}
      >
        Card image {initial?.id ? "(leave empty to keep current image)" : ""}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <label style={{ padding: "7px 11px", border: "1px solid #94a3b8", borderRadius: 6, background: "#f8fafc", color: "#1e293b", fontSize: 13, cursor: "pointer" }}>
          Choose File
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) setImageToCrop(selectedFile);
              e.target.value = "";
            }}
          />
        </label>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: files.image ? "#475569" : "#94a3b8" }}>
          {files.image ? files.image.name : "No file chosen"}
        </span>
      </div>
      {files.image && <p style={{ margin: "7px 0 0", fontSize: 12, color: "#16a34a" }}>Cropped image ready — 1254 × 1254 px</p>}
      <label
        style={{
          display: "block",
          fontSize: 13,
          fontWeight: 600,
          color: "#374151",
          margin: "15px 0 5px",
        }}
      >
        Icon {initial?.id ? "(leave empty to keep current icon)" : ""}
      </label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => setFiles({ ...files, icon: e.target.files?.[0] })}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 9,
          marginTop: 22,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "8px 15px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          disabled={saving}
          style={{
            padding: "8px 15px",
            borderRadius: 8,
            border: 0,
            background: "#3b82f6",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {saving
            ? "Saving..."
            : initial?.id
              ? "Save changes"
              : "Create industry"}
        </button>
      </div>
    </form>
    {imageToCrop && <IndustryImageCropper file={imageToCrop} onCancel={() => setImageToCrop(null)} onComplete={(croppedFile) => { setFiles({ ...files, image: croppedFile }); setImageToCrop(null); }} />}
    </>
  );
}
export default function IndustriesManager() {
  const [rows, setRows] = useState([]),
    [loading, setLoading] = useState(true),
    [query, setQuery] = useState(""),
    [modal, setModal] = useState(null),
    [saving, setSaving] = useState(false),
    [notice, setNotice] = useState(""),
    [preview, setPreview] = useState(null);
  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.list(q);
      setRows(data?.results ?? data?.data ?? []);
    } catch {
      setNotice("Could not load industries.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => load(query), 250);
    return () => clearTimeout(timer);
  }, [query, load]);
  const save = async (form, files) => {
    setSaving(true);
    try {
      const body = new FormData();
      body.append("name", form.name.trim());
      body.append("description", form.description.trim());
      if (files.image) body.append("image", files.image);
      if (files.icon) body.append("icon", files.icon);
      if (modal?.id) await api.update(modal.id, body);
      else await api.create(body);
      setModal(null);
      setNotice(`Industry ${modal?.id ? "updated" : "created"} successfully.`);
      load(query);
    } catch (err) {
      setNotice(err?.response?.data?.detail || "Could not save the industry.");
    } finally {
      setSaving(false);
    }
  };
  const togglePublish = async (row) => {
    try {
      const body = new FormData();
      body.append("is_published", String(!row.is_published));
      await api.update(row.id, body);
      setNotice(
        `Industry ${row.is_published ? "unpublished" : "published"} successfully.`,
      );
      load(query);
    } catch {
      setNotice("Could not update publishing status.");
    }
  };
  const toggleStatus = async (row) => {
    try {
      const body = new FormData();
      body.append("is_active", String(!row.is_active));
      await api.update(row.id, body);
      setNotice(
        `Industry ${row.is_active ? "deactivated" : "activated"} successfully.`,
      );
      load(query);
    } catch {
      setNotice("Could not update industry status.");
    }
  };
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "28px 30px",
        background: "#f8fafc",
        color: "#1e293b",
        fontFamily: "Inter,Arial,sans-serif",
      }}
    >
      <style>{`@keyframes industry-skeleton-pulse{0%,100%{opacity:.55}50%{opacity:1}}.industry-skeleton{background:#e2e8f0;animation:industry-skeleton-pulse 1.2s ease-in-out infinite}.industry-action:hover{background:#f8fafc!important}.industry-primary:hover{background:#2563eb!important}`}</style>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Industries
          </h1>
          <p style={{ margin: "5px 0 0", fontSize: 13, color: "#64748b" }}>
            Manage the industries displayed across the public website.
          </p>
        </div>
        <button
          className="industry-primary"
          onClick={() => setModal({})}
          style={{
            border: 0,
            borderRadius: 8,
            background: "#3b82f6",
            color: "#fff",
            padding: "9px 14px",
            display: "inline-flex",
            gap: 7,
            alignItems: "center",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <PlusIcon /> Create Industry
        </button>
      </div>
      {notice && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: 16,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {notice}
        </div>
      )}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,.05)",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ position: "relative", width: "min(340px,100%)" }}>
            <span
              style={{
                position: "absolute",
                left: 11,
                top: 10,
                color: "#94a3b8",
              }}
            >
              <SearchIcon />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search industries..."
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "9px 12px 9px 34px",
                border: "1.5px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
          </div>
          <span style={{ fontSize: 13, color: "#64748b", paddingTop: 9 }}>
            {rows.length} industr{rows.length === 1 ? "y" : "ies"}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr
                style={{
                  background: "#f8fafc",
                  color: "#64748b",
                  textAlign: "left",
                }}
              >
                <th style={{ padding: "12px 20px", fontWeight: 600 }}>Image</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Icon</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>
                  Industry name
                </th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>
                  Status
                </th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>
                  Publish / Unpublish
                </th>
                <th
                  style={{
                    padding: "12px 20px",
                    fontWeight: 600,
                    textAlign: "right",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <IndustryTableSkeleton />
              ) : !rows.length ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      padding: 36,
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    No industries found. Create your first industry to publish
                    it on the website.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px 20px" }}>
                      <div
                        style={{
                          width: 92,
                          height: 42,
                          border: "1px solid #f1f5f9",
                          borderRadius: 7,
                          overflow: "hidden",
                          background: "#fff",
                        }}
                      >
                        {row.image && (
                          <button
                            type="button"
                            onClick={() =>
                              setPreview({
                                src: row.image,
                                name: `${row.name} image`,
                              })
                            }
                            style={{
                              width: "100%",
                              height: "100%",
                              border: 0,
                              padding: 0,
                              background: "none",
                              cursor: "zoom-in",
                            }}
                          >
                            <img
                              src={row.image}
                              alt={`${row.name} preview`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          border: "1px solid #f1f5f9",
                          borderRadius: 7,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        {row.icon && (
                          <button
                            type="button"
                            onClick={() =>
                              setPreview({
                                src: row.icon,
                                name: `${row.name} icon`,
                              })
                            }
                            style={{
                              border: 0,
                              padding: 0,
                              background: "none",
                              cursor: "zoom-in",
                            }}
                          >
                            <img
                              src={row.icon}
                              alt={`${row.name} icon preview`}
                              style={{
                                width: 26,
                                height: 26,
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          </button>
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        fontWeight: 600,
                        color: "#0f172a",
                      }}
                    >
                      {row.name}
                      <div
                        style={{
                          fontWeight: 400,
                          fontSize: 12,
                          color: "#64748b",
                          marginTop: 3,
                        }}
                      >
                        {row.description || row.slug}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <StatusBadge active={row.is_active} />
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <button
                        className="industry-action"
                        onClick={() => togglePublish(row)}
                        style={{
                          padding: "6px 10px",
                          background: row.is_published ? "#fff7ed" : "#f0fdf4",
                          border: `1px solid ${row.is_published ? "#fed7aa" : "#bbf7d0"}`,
                          borderRadius: 7,
                          color: row.is_published ? "#c2410c" : "#15803d",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {row.is_published ? "Unpublish" : "Publish"}
                      </button>
                    </td>
                    <td
                      style={{
                        padding: "12px 20px",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <button
                        className="industry-action"
                        onClick={() => setModal(row)}
                        style={{
                          padding: "6px 9px",
                          background: "#fff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 7,
                          color: "#2563eb",
                          cursor: "pointer",
                          marginRight: 7,
                        }}
                        title="Edit industry"
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="industry-action"
                        onClick={() => toggleStatus(row)}
                        style={{
                          padding: "6px 10px",
                          background: row.is_active ? "#fef2f2" : "#f0fdf4",
                          border: `1px solid ${row.is_active ? "#fecaca" : "#bbf7d0"}`,
                          borderRadius: 7,
                          color: row.is_active ? "#dc2626" : "#15803d",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {row.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {modal && (
        <Modal
          title={modal.id ? "Edit Industry" : "Create Industry"}
          onClose={() => setModal(null)}
        >
          <IndustryForm
            initial={modal.id ? modal : null}
            saving={saving}
            onSubmit={save}
            onCancel={() => setModal(null)}
          />
        </Modal>
      )}
      {preview && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={preview.name}
          onClick={() => setPreview(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            padding: 24,
            background: "rgba(15,23,42,.88)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={preview.src}
            alt={preview.name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: 10,
              background: "#fff",
              cursor: "default",
            }}
          />
          <button
            type="button"
            onClick={() => setPreview(null)}
            style={{
              position: "fixed",
              top: 18,
              right: 18,
              border: "1px solid #fff",
              borderRadius: 8,
              padding: "7px 11px",
              background: "rgba(15,23,42,.65)",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
